/**
 * GameDetailPage — read-only game overview at /games/:gameId.
 *
 * This is a hub, not a form: result entry and approval live on their own
 * wireframe-aligned pages (game-result-entry.html / game-result-approve.html).
 * Whatever source page the user arrives from, the contextual CTA here routes
 * them to the SAME unified pages — "Enter result" → /games/:gameId/result and
 * "Review & approve" → /games/:gameId/approve — so the experience is identical
 * everywhere. (Previously this page embedded a different, off-spec inline form.)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGameById, adminApproveResultOnBehalf } from '../../store/slices/gameSlice';
import { fetchMyTeams } from '../../store/slices/teamSlice';
import { GameStatus, UserRole } from '@sulis-liiga/common';
import GameInfo from '../../components/game/GameInfo';
import { teamLabel } from '../../utils/gameTeams';

const GameDetailPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { gameId } = useParams<{ gameId: string }>();
  const { currentGame: game, loading, error } = useSelector((state: RootState) => state.game);
  const { myTeams } = useSelector((state: RootState) => state.team);
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { selectedTenantSlug } = useSelector((state: RootState) => state.ui);

  useEffect(() => {
    if (gameId) {
      dispatch(fetchGameById(gameId));
    }
  }, [dispatch, gameId]);

  // My teams gate the contextual CTAs. Only fetch once a tenant is active so a
  // deep-link to /games/:gameId doesn't leak the league-scoped call to the registry.
  useEffect(() => {
    if (isAuthenticated && selectedTenantSlug) {
      dispatch(fetchMyTeams());
    }
  }, [dispatch, isAuthenticated, selectedTenantSlug]);

  const myTeamIds = useMemo(() => new Set(myTeams.map(t => t.id)), [myTeams]);

  const isAdmin =
    isAuthenticated &&
    (user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
      user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR));

  // Admin "approve on behalf" confirmation modal state.
  const [showAdminApprove, setShowAdminApprove] = useState(false);
  const [adminApproving, setAdminApproving] = useState(false);
  const [adminApproveError, setAdminApproveError] = useState<string | null>(null);

  const confirmAdminApprove = async (): Promise<void> => {
    if (!gameId) return;
    setAdminApproving(true);
    setAdminApproveError(null);
    try {
      await dispatch(adminApproveResultOnBehalf(gameId)).unwrap();
      setShowAdminApprove(false);
    } catch (err) {
      setAdminApproveError(typeof err === 'string' ? err : 'Failed to approve the result.');
    } finally {
      setAdminApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="rounded-md bg-danger-50 p-4">
          <p className="text-sm font-medium text-danger-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Game not found</p>
      </div>
    );
  }

  // ── Contextual result action ────────────────────────────────────────────────
  // Mirrors GamesPage gating so the detail page and the list agree on who sees what.
  const isMyGame = game.teamIds.some(id => myTeamIds.has(id));
  const myTeamId = game.teamIds.find(id => myTeamIds.has(id));
  const opponentTeamId = game.teamIds.find(id => id !== myTeamId) ?? game.teamIds[1];
  const opponentLabel = teamLabel(game, opponentTeamId);
  const myVerification =
    game.result && user ? game.result.verifications.find(v => v.userId === user.id) : undefined;
  const awaitingMyApproval =
    !!game.result &&
    !game.result.verified &&
    game.status !== GameStatus.DISPUTED &&
    isMyGame &&
    !myVerification;

  // A league admin may finalize a pending (unverified, non-disputed) result on
  // behalf of the players. Not shown when the admin is themselves the one who
  // still needs to approve — they use the normal "Review & approve" flow then.
  const canAdminApprove =
    !!isAdmin &&
    !!game.result &&
    !game.result.verified &&
    game.status !== GameStatus.DISPUTED &&
    !awaitingMyApproval;

  const matchLabel = `${teamLabel(game, game.teamIds[0])} vs ${teamLabel(game, game.teamIds[1])}`;

  const renderResultAction = (): React.ReactNode => {
    // Scheduled, no result yet → enter result (participants only).
    if (game.status === GameStatus.SCHEDULED && !game.result) {
      if (!isMyGame) return null;
      return (
        <div className="bg-white shadow rounded-lg p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Game played?</h3>
            <p className="mt-1 text-sm text-gray-500">
              Enter the game scores and submit the result to {opponentLabel} for approval.
            </p>
          </div>
          <Link
            to={`/games/${game.id}/result`}
            className="shrink-0 inline-flex justify-center px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Enter result
          </Link>
        </div>
      );
    }

    // Result submitted, awaiting approval.
    if (!!game.result && !game.result.verified && game.status !== GameStatus.DISPUTED) {
      if (awaitingMyApproval) {
        return (
          <div className="bg-white shadow rounded-lg p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-gray-900">A result needs your approval</h3>
              <p className="mt-1 text-sm text-gray-500">
                {opponentLabel} submitted a result for this game. Review it and approve, or dispute
                if it&apos;s wrong.
              </p>
            </div>
            <Link
              to={`/games/${game.id}/approve`}
              className="shrink-0 inline-flex justify-center px-5 py-2.5 rounded-md text-sm font-medium text-white bg-success-600 hover:bg-success-800 transition-colors"
            >
              Review &amp; approve
            </Link>
          </div>
        );
      }
      // I (or my team) submitted — waiting on the opponent.
      if (isMyGame) {
        return (
          <div className="rounded-md bg-primary-50 text-primary-800 text-sm p-4">
            Result submitted. Waiting for <strong>{opponentLabel}</strong> to approve before points
            are awarded.
          </div>
        );
      }
      return (
        <div className="rounded-md bg-primary-50 text-primary-800 text-sm p-4">
          A result has been submitted and is awaiting approval from the participating teams.
        </div>
      );
    }

    // Disputed. A league admin gets a Resolve action into the shared Edit-a-Game
    // page (returning here afterwards); everyone else sees a waiting notice.
    if (game.status === GameStatus.DISPUTED) {
      if (isAdmin) {
        return (
          <div className="bg-white shadow rounded-lg p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-gray-900">This result is disputed</h3>
              <p className="mt-1 text-sm text-gray-500">
                Review the score, correct it if needed, and save to resolve the dispute.
              </p>
            </div>
            <Link
              to={`/games/${game.id}/edit?returnTo=${encodeURIComponent(`/games/${game.id}`)}`}
              className="shrink-0 inline-flex justify-center px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Resolve
            </Link>
          </div>
        );
      }
      return (
        <div className="rounded-md bg-yellow-50 text-yellow-800 text-sm p-4">
          This result is <strong>disputed</strong>. A league administrator will review and resolve
          it.
        </div>
      );
    }

    return null;
  };

  const resultAction = renderResultAction();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <GameInfo game={game} />
        {resultAction}

        {canAdminApprove && (
          <div className="bg-white shadow rounded-lg p-6 border border-yellow-300 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Admin: approve on behalf</h3>
              <p className="mt-1 text-sm text-gray-500">
                As a league admin you can finalize this pending result for the players. This awards
                points immediately.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setAdminApproveError(null);
                setShowAdminApprove(true);
              }}
              className="shrink-0 inline-flex justify-center px-5 py-2.5 rounded-md text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 transition-colors"
            >
              Approve on behalf
            </button>
          </div>
        )}
      </div>

      {showAdminApprove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-approve-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 id="admin-approve-title" className="text-lg font-semibold text-gray-900">
              Approve on someone else&apos;s behalf?
            </h3>
            <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-3">
              <p className="text-sm text-yellow-800">
                You are approving this result <strong>on behalf of the players</strong> in{' '}
                <strong>{matchLabel}</strong>. This finalizes the game and awards points without the
                players&apos; own approval. The action is recorded in the game&apos;s history.
              </p>
            </div>
            {adminApproveError && (
              <p className="mt-3 text-sm text-danger-700">{adminApproveError}</p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowAdminApprove(false)}
                disabled={adminApproving}
                className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAdminApprove}
                disabled={adminApproving}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50"
              >
                {adminApproving ? 'Approving…' : 'Yes, approve on their behalf'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameDetailPage;
